import { useState, type ReactNode } from "react";
import { segmentHowRelayWorks, howRelayWorksSlugFromHref } from "../lib/howRelayWorksLinks";
import { parseLogLinks, isRunnerImageUrl, type LogLinkSegment } from "../lib/logLinks";
import { HowRelayWorksLink } from "./HowRelayWorksText";

const LINK_STYLE = {
  color: "var(--er-accent)",
  textDecoration: "underline",
  overflowWrap: "anywhere",
  wordBreak: "break-all",
} as const;

function Anchor({ url, label }: { url: string; label: string }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
      {label}
    </a>
  );
}

function Thumb({ url, label }: { url: string; label: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure style={{ margin: 0, flex: "0 1 320px", minWidth: 0, maxWidth: 320 }}>
      {failed ? (
        <div style={{ fontSize: 12 }}>
          Image expired or unavailable <Anchor url={url} label={label} />
        </div>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
            style={{ display: "block", width: "100%", maxWidth: 320, border: "1px solid var(--border)", borderRadius: 4 }}
          />
        </a>
      )}
      <figcaption style={{ fontSize: 11, color: "var(--text3)" }}>{label}</figcaption>
    </figure>
  );
}

type Link = Extract<LogLinkSegment, { kind: "link" }>;

/**
 * Post body text with safe, clickable links. With `embedImages` (visual-check
 * results), runner-bucket links show as thumbnails; adjacent ones sit side by side.
 */
export function LogLinkText({ text, embedImages = false }: { text: string; embedImages?: boolean }) {
  const nodes: ReactNode[] = [];
  let key = 0;
  const emit = (segs: LogLinkSegment[]) => {
    for (let i = 0; i < segs.length; i++) {
      const s = segs[i];
      if (s.kind === "text") {
        nodes.push(s.text);
      } else if (embedImages && isRunnerImageUrl(s.url)) {
        const group: Link[] = [s];
        // Gather following image links separated only by whitespace or "|".
        while (i + 2 < segs.length) {
          const sep = segs[i + 1];
          const next = segs[i + 2];
          if (sep.kind === "text" && /^[\s|]*$/.test(sep.text) && next.kind === "link" && isRunnerImageUrl(next.url)) {
            group.push(next);
            i += 2;
          } else break;
        }
        nodes.push(
          <div key={key++} style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "6px 0", whiteSpace: "normal" }}>
            {group.map((g) => <Thumb key={g.url} url={g.url} label={g.label} />)}
          </div>,
        );
      } else if (howRelayWorksSlugFromHref(s.url)) {
        nodes.push(<HowRelayWorksLink key={key++} slug={howRelayWorksSlugFromHref(s.url)!}>{s.label}</HowRelayWorksLink>);
      } else {
        nodes.push(<Anchor key={key++} url={s.url} label={s.label} />);
      }
    }
  };
  // Parse links over the whole text first so "|" separators stay visible to grouping.
  // How Relay Works /#slug links are handled inside the plain-text runs.
  let pending: LogLinkSegment[] = [];
  const flush = () => {
    if (pending.length) emit(pending);
    pending = [];
  };
  for (const s of parseLogLinks(text)) {
    const parts = s.kind === "text" ? segmentHowRelayWorks(s.text) : [];
    if (s.kind === "link" || parts.every((p) => typeof p === "string")) {
      pending.push(s);
      continue;
    }
    flush();
    for (const p of parts) {
      if (typeof p === "string") nodes.push(p);
      else nodes.push(<HowRelayWorksLink key={key++} slug={p.slug}>{p.label}</HowRelayWorksLink>);
    }
  }
  flush();
  return <>{nodes}</>;
}
